// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useState } from 'react';

import { Box } from '@mantine/core';

import { DesktopLayoutCard } from 'app/Layout/DesktopLayoutCard';
import { StageColorScheme } from 'app/Layout/StageColorScheme';

import classes from './DesktopRouteLayout.module.css';

type Props = {
  route: string;
  /** Whether the route renders on the dark stage behind the card instead of on the card. */
  stage: boolean;
  children: ReactNode;
};

/**
 * The desktop frame around a route: the white page card, and for a stage route the dark stage the card slides away
 * from. Every route renders through this one element, so React keeps it mounted across navigations and the card can
 * animate between the two. The surface being left keeps showing its last page until the card has finished moving.
 */
export const DesktopRouteLayout = ({ route, stage, children }: Props) => {
  const [current, setCurrent] = useState({ route, stage, children });
  const [leavingPage, setLeavingPage] = useState<ReactNode>(null);
  const [animate, setAnimate] = useState(false);
  if (route !== current.route) {
    if (stage !== current.stage) {
      setLeavingPage(current.children);
      setAnimate(true);
    }
    setCurrent({ route, stage, children });
  }
  const stagePage = stage ? children : leavingPage;

  return (
    <Box className={classes.host} mod={{ stage, animate, transitioning: leavingPage !== null }}>
      <Box className={classes.stage} inert={!stage}>
        <Box className={classes.divider} />
        {stagePage !== null && <StageColorScheme className={classes.stageContent}>{stagePage}</StageColorScheme>}
      </Box>
      <Box
        className={classes.card}
        inert={stage}
        onTransitionEnd={(event) => {
          if (event.target === event.currentTarget && event.propertyName === 'opacity') setLeavingPage(null);
        }}
      >
        <DesktopLayoutCard>{stage ? leavingPage : children}</DesktopLayoutCard>
      </Box>
    </Box>
  );
};
